// Die Firmenauswahl — Suche oben, die Firma(en) der offenen Seite als Knopf darunter.
//
// Sie steht NICHT mehr vor jedem Aktivitaets-Fenster: eine einzelne vorgeschlagene
// Firma wird uebernommen und das Formular geht direkt auf (needsCompanyPicker in
// pageCompany.js). Uebrig bleiben die Faelle mit einer echten Entscheidung — mehrere
// Firmen auf der Seite, der Anruf ohne Vorschlag, und der Firmenwechsel aus dem
// Anruf- oder Mail-Fenster heraus.
import React, { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import CompanySearch from "./CompanySearch";
import { client } from "../helpers/amplifyClient";
import * as queries from "./graphql/activityFeed.queries";

// Nur Kunde und Lead: an allem anderen (Lieferant, Spediteur) haengt keine Aktivitaet.
export const ALLOWED_TYPES = ["customer", "lead"];

export const loadCompanyContacts = async (id) => {
  const res = await client.graphql({
    query: queries.companiesContactPersonsByCompanyID,
    variables: { companyID: id },
  });
  return res.data?.companiesContactPersonsByCompanyID?.items || [];
};

export const loadCompany = async (id) => {
  const res = await client.graphql({
    query: queries.getCompanyCallInfo,
    variables: { id },
  });
  return res.data?.getCompanies || null;
};

/**
 * @param suggested [{id, name?}] — die Firmen der offenen Seite. Eine Sendung oder
 *   Luftfracht haengt an mehreren; dann stehen sie alle zur Wahl. Ohne `name` wird
 *   die Firma nachgeladen; ist sie dann kein Kunde/Lead, faellt sie weg.
 * @param onPick ({id, name}) => void
 * @param onSkip — nur gesetzt, wo die Firma optional ist (Aufgabe, Mail).
 */
const ActivityCompanyModal = ({ suggested, onPick, onClose, onSkip, busy }) => {
  const { t } = useTranslation(["components", "common", "tasks"]);
  const list = (Array.isArray(suggested) ? suggested : [suggested]).filter((c) => c?.id);
  const missing = list.filter((c) => !c.name).map((c) => c.id);
  const [quick, setQuick] = useState(list.filter((c) => c.name));
  const [resolving, setResolving] = useState(missing.length > 0);
  // Die Liste ist bei jedem Rendern neu; der Schluessel ist es nicht.
  const key = list.map((c) => c.id).join(",");

  useEffect(() => {
    if (!missing.length) return undefined;
    let cancelled = false;
    Promise.all(missing.map((id) => loadCompany(id).catch(() => null)))
      .then((loaded) => {
        if (cancelled) return;
        // Reihenfolge der Seite behalten: erst die mitgelieferten, dann die geladenen.
        setQuick((known) => [
          ...known,
          ...loaded.filter((c) => c?.name && ALLOWED_TYPES.includes(c.type)),
        ]);
      })
      .catch((error) => {
        // Nicht ladbar heisst: nur suchen. Kein Toast, der Nutzer hat nichts falsch gemacht.
        console.error("ActivityCompanyModal: suggestion load failed", error);
      })
      .finally(() => !cancelled && setResolving(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <Modal show onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h5">{t("Select a company")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <CompanySearch
          type="customerOrLead"
          allowLeads
          placeholder={t("Search company...", { ns: "tasks" })}
          onSelect={(option) => {
            const id = option?.value || option?.data?.id;
            if (id) onPick({ id, name: option?.data?.name || " " });
          }}
        />

        {(resolving || busy) && (
          <div className="text-center mt-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {quick.length > 0 && (
          <>
            <div className="text-muted small mt-3 mb-1">{t("On this page")}</div>
            <div className="d-flex flex-column gap-1">
              {quick.map((company) => (
                <Button
                  key={company.id}
                  variant="light"
                  className="w-100 text-start d-flex align-items-center gap-2"
                  onClick={() => onPick({ id: company.id, name: company.name })}
                >
                  <i className="uil-suitcase font-16" />
                  <span>
                    {company.number ? `${company.number} - ${company.name}` : company.name}
                  </span>
                </Button>
              ))}
            </div>
          </>
        )}

        {onSkip && (
          <div className="mt-3">
            <Button variant="link" className="p-0" onClick={onSkip}>
              {t("Continue without a company")}
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ActivityCompanyModal;
